import CodeIcon from '@mui/icons-material/Code';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';

type FormatDef = {
    label: string;
    icon: React.ReactNode;
    syntax: string; // e.g. "*"
    placeholder: string; // inserted when no text is selected
    testId: string;
};

const FORMATS: FormatDef[] = [
    {
        label: 'Bold',
        icon: <FormatBoldIcon fontSize="small" />,
        syntax: '*',
        placeholder: 'bold',
        testId: 'format-bold',
    },
    {
        label: 'Italic',
        icon: <FormatItalicIcon fontSize="small" />,
        syntax: '_',
        placeholder: 'italic',
        testId: 'format-italic',
    },
    {
        label: 'Strikethrough',
        icon: <StrikethroughSIcon fontSize="small" />,
        syntax: '~',
        placeholder: 'strikethrough',
        testId: 'format-strikethrough',
    },
    {
        label: 'Monospace',
        icon: <CodeIcon fontSize="small" />,
        syntax: '```',
        placeholder: 'monospace',
        testId: 'format-monospace',
    },
];

export type Props = {
    textareaRef: React.RefObject<HTMLTextAreaElement>;
    value: string;
    onChange: (newValue: string) => void;
};

/**
 * Wraps the currently selected text in `syntax` markers.
 * If nothing is selected, inserts `syntax + placeholder + syntax` at the cursor.
 */
export const applyFormat = (
    value: string,
    selectionStart: number,
    selectionEnd: number,
    syntax: string,
    placeholder: string
): { newValue: string; newSelectionStart: number; newSelectionEnd: number } => {
    const selected = value.slice(selectionStart, selectionEnd);
    const insert = selected || placeholder;
    const newValue = value.slice(0, selectionStart) + syntax + insert + syntax + value.slice(selectionEnd);
    const newSelectionStart = selectionStart + syntax.length;
    const newSelectionEnd = newSelectionStart + insert.length;
    return { newValue, newSelectionStart, newSelectionEnd };
};

const WhatsAppFormattingToolbar = ({ textareaRef, value, onChange }: Props) => {
    const handleFormat = (format: FormatDef) => {
        const textarea = textareaRef.current;
        const start = textarea?.selectionStart ?? value.length;
        const end = textarea?.selectionEnd ?? value.length;

        const { newValue, newSelectionStart, newSelectionEnd } = applyFormat(
            value,
            start,
            end,
            format.syntax,
            format.placeholder
        );

        onChange(newValue);

        // Restore focus and selection after React re-render
        requestAnimationFrame(() => {
            if (textarea) {
                textarea.focus();
                textarea.setSelectionRange(newSelectionStart, newSelectionEnd);
            }
        });
    };

    return (
        <Stack direction="row" alignItems="center" spacing={0.5} data-testid="whatsapp-formatting-toolbar">
            {FORMATS.map((fmt) => (
                <Tooltip key={fmt.testId} title={fmt.label} arrow>
                    <IconButton
                        size="small"
                        aria-label={fmt.label}
                        data-testid={fmt.testId}
                        onMouseDown={(e) => {
                            // Prevent textarea from losing selection on mouse click
                            e.preventDefault();
                            handleFormat(fmt);
                        }}
                    >
                        {fmt.icon}
                    </IconButton>
                </Tooltip>
            ))}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                *bold*&nbsp; _italic_&nbsp; ~strike~&nbsp; ```mono```
            </Typography>
        </Stack>
    );
};

export default WhatsAppFormattingToolbar;
